import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { baseUrl, headersDefault } from "../../redux/actions";
import { getAuthToken } from "../../services/authStorage";
import { setAdoc_agreements } from "../../redux/reducer/userSlice";

// Maps the URL slug to a predicate that finds the matching legal document
// inside an agreement's `documents[]` array. We match by title substring
// because document IDs differ between staging and production seeds — the
// Italian titles are stable copy supplied by the legal team and unlikely to
// change without coordination.
export const DOC_MATCHERS = {
  "privacy-policy": {
    title: "Informativa sulla privacy",
    match: (doc) => /privacy|informativa/i.test(doc?.title || ""),
  },
  terms: {
    title: "Termini di utilizzo",
    match: (doc) => /termini|condizioni/i.test(doc?.title || ""),
  },
};

// `key` of the agreement that bundles the Privacy Policy and the Terms &
// Conditions documents in `/api/v1/users/agreements/list`. Other agreements
// (marketing communications, wellness news) carry no documents — so this
// constant intentionally pins us to the only one that does.
const PRIMARY_AGREEMENT_KEY = "terms_and_conditions";

const findDocument = (agreements, slug) => {
  const matcher = DOC_MATCHERS[slug];
  if (!matcher || !Array.isArray(agreements)) return null;

  // First try the primary "terms_and_conditions" agreement — this is
  // where the privacy + terms documents live for full premium users.
  const primary = agreements.find((a) => a?.key === PRIMARY_AGREEMENT_KEY);
  if (primary?.documents) {
    const found = primary.documents.find(matcher.match);
    if (found) return found;
  }

  // Fallback: scan EVERY agreement's documents[]. For guest-tier users
  // the backend filters out (or renames) the "terms_and_conditions"
  // agreement, so the previous primary-only lookup returned null and
  // the page rendered "Documento non disponibile." Even if the
  // privacy/terms documents are nested under a different agreement
  // key (e.g. a marketing-communications or wellness-news entry),
  // the title-based matcher will still find them here.
  for (const agreement of agreements) {
    const docs = agreement?.documents;
    if (!Array.isArray(docs)) continue;
    const found = docs.find(matcher.match);
    if (found) return found;
  }

  // Diagnostic — log to console on misses so we can see exactly which
  // combination caused the "Documento non disponibile" message. Kept
  // enabled in production: the rendered error is generic but this log
  // gives the next investigation a concrete agreement-shape dump.
  // eslint-disable-next-line no-console
  console.warn("[LegalPage] no document matched", {
    slug,
    agreementKeys: agreements.map((a) => a?.key),
    docCounts: agreements.map((a) => ({
      key: a?.key,
      docs: Array.isArray(a?.documents) ? a.documents.length : 0,
      titles: Array.isArray(a?.documents)
        ? a.documents.map((d) => d?.title)
        : [],
    })),
  });

  return null;
};

/**
 * One of the CRM's legal documents ("terms" or "privacy-policy"), fetched the
 * way the /legal reader always has. Shared with the in-place reader the
 * purchase confirmations open, so a member reads the same text either way.
 *
 * @param {string} slug
 * @returns {{ matcher: object|undefined, document: object|null, loading: boolean, fetchError: string|null }}
 */
export const useLegalDocument = (slug) => {
  const dispatch = useDispatch();
  const agreements = useSelector((s) => s.user?.agreements);
  const matcher = DOC_MATCHERS[slug];

  // Always refetch on mount, even if Redux already has agreements.
  // The previous "skip when length > 0" guard caused a real bug: a
  // guest user whose earlier session populated `agreements` from a
  // narrower endpoint (no documents attached) would land here with
  // `length > 0` → fetch skipped → findDocument returns null → red
  // "Documento non disponibile" error. Symptom: clicking the privacy
  // / terms link from the paywall failed, while a full URL reload
  // (which bypassed the stale Redux cache via re-bootstrap) worked.
  // Refetching once on mount is cheap (single small JSON) and makes
  // the page self-consistent regardless of how the user arrived.
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [localAgreements, setLocalAgreements] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(null);

    // The sign-in screen links here before there is an account ("Continuando
    // accetti i Termini e Condizioni…"), and the member's own list needs a
    // token. Signed out, the public list has the same documents.
    const signedIn = !!getAuthToken();

    fetch(`${baseUrl}/api/v1/users/agreements/${signedIn ? "list" : "public"}`, {
      headers: headersDefault(),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
          ? json
          : [];
        if (cancelled) return;
        setLocalAgreements(data);
        // Mirror to Redux so other consumers (e.g. the onboarding
        // agreements modal) see the fresh copy too. We never replace
        // an existing populated list with an empty one, defensively.
        if (signedIn && data.length > 0) {
          dispatch(setAdoc_agreements(data));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(err?.message || "fetch failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Intentionally empty deps — fetch once per mount. The slug
    // doesn't change while this component is mounted (slug change
    // unmounts + remounts via React Router's key behavior).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefer the freshly-fetched list over the (possibly stale) Redux
  // copy. Falls back to Redux while the fetch is in flight so the
  // page can render instantly when the cache happens to be valid.
  const effectiveAgreements =
    localAgreements && localAgreements.length > 0
      ? localAgreements
      : agreements;

  const document = useMemo(
    () => findDocument(effectiveAgreements, slug),
    [effectiveAgreements, slug],
  );

  return { matcher, document, loading, fetchError };
};
